<template>
  <UFieldGroup>
    <UButton icon="lucide:refresh-cw" :loading="loading" @click="refresh" />
    <UButton @click="today">
      {{ $t('datetime.labels.today') }}
    </UButton>
    <UButton
      v-if="!isMonth"
      icon="lucide:chevrons-left"
      @click="move(-7)"
    />
    <UButton
      v-if="!isRange"
      icon="lucide:chevron-left"
      @click="move(-1)"
    />
    <UButton
      as="span"
      aria-live="polite"
      class="cursor-default select-none hover:bg-elevated active:bg-elevated"
    >
      {{ label }}
    </UButton>
    <UButton
      v-if="!isRange"
      icon="lucide:chevron-right"
      @click="move(1)"
    />
    <UButton
      v-if="!isMonth"
      icon="lucide:chevrons-right"
      @click="move(7)"
    />
  </UFieldGroup>
</template>

<script setup lang="ts">
const { $date, $dateFns } = useNuxtApp()

interface Props {
  dates: any[]
  options: any
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<{
  (e: 'update:dates', value: any[]): void
  (e: 'refresh'): void
}>()

const _dates = computed({
  get: () => props.dates,
  set: (value: any[]) => emit('update:dates', value),
})

const isRange = computed(() => props.options.date.mode === 'range')
const isMonth = computed(() => props.options.date.mode === 'month')

const label = computed(() => {
  const dates = _dates.value
  const format = isMonth.value ? 'MMMM yyyy' : 'EEE, do MMM yyyy'
  const firstDate = $date.parse(dates[0])

  if (isRange.value) {
    const lastDate = $date.parse(dates[dates.length - 1])
    const lastFormatted = $date.format(lastDate, format)

    let firstFormatted
    if ($dateFns.isSameMonth(firstDate, lastDate)) {
      firstFormatted = $date.format(firstDate, 'EEE, do')
    } else {
      firstFormatted = $date.format(firstDate, format)
    }

    return `${firstFormatted} - ${lastFormatted}`
  }

  return $date.format(firstDate, format)
})

function move(i: number) {
  const date = _dates.value[0]
  const timeframe = isMonth.value ? { months: i } : { days: i }
  const m = $date.add(date, timeframe, 'yyyyMMdd', 'yyyyMMdd')
  _dates.value = [m]
}

function today() {
  const t = $date.format(new Date())
  _dates.value = [t]
}

function refresh() {
  emit('refresh')
}
</script>
